import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SavedDataInfo } from '../../../../models/form/saved-data-info';
import { AlertInfo } from '../../../../models/alert-info';
import { EmployeeConsultantsService } from '../../../../services/api/hr/employee-consultants.service';
import { EmployeeConsultantsResidentialInfoFormComponent } from '../components/employee-consultants-residential-info-form/employee-consultants-residential-info-form.component';
import { EmployeeConsultantsGeneralInfoFormComponent } from '../components/employee-consultants-general-info-form/employee-consultants-general-info-form.component';
import { EmployeeConsultantsPreviousJobsFormComponent } from '../components/employee-consultants-previous-jobs-form/employee-consultants-previous-jobs-form.component';
import { HttpParams } from '@angular/common/http';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';

import { FormNotesCustomersComponent } from 'src/app/modules/customers/components/notes-customers-form/notes-customers-form.component';
import { EmployeeDocumentsComponent } from '../components/employee-documents/employee-documents.component';

@Component({
  selector: 'app-employee-consultants-edit-page',
  imports: [NgbAlert, EmployeeConsultantsResidentialInfoFormComponent, EmployeeConsultantsGeneralInfoFormComponent, EmployeeConsultantsPreviousJobsFormComponent, FormNotesCustomersComponent, EmployeeDocumentsComponent],
  templateUrl: './employee-consultants-edit-page.component.html',
  styleUrl: './employee-consultants-edit-page.component.scss'
})
export class EmployeeConsultantsEditPageComponent {
  employeeId!: number;
  employee?: any;
  employeeNotes?: any;
  employeeWorkCustomerList: any[] = [];
  readonly: boolean = false;
  alertInfo: AlertInfo = { show: false, type: 'success', message: '' };
  
  private alertTimers: Map<string, any> = new Map<string, any>();

  @ViewChild(EmployeeConsultantsResidentialInfoFormComponent) residentialInfoFormComponent!: EmployeeConsultantsResidentialInfoFormComponent;
  @ViewChild(EmployeeConsultantsGeneralInfoFormComponent) generalInfoFormComponent!: EmployeeConsultantsGeneralInfoFormComponent;
  @ViewChild(EmployeeConsultantsPreviousJobsFormComponent) previousJobsFormComponent!: EmployeeConsultantsPreviousJobsFormComponent;
  @ViewChild(EmployeeDocumentsComponent) documentsComponent!: EmployeeDocumentsComponent;
  
  isSaving: boolean = false;
  
  constructor(
    private route: ActivatedRoute,
    protected employeeConsultantsService: EmployeeConsultantsService,
    private alertNotificationService: AlertNotificationService,
  ) {}
  
  ngOnInit(): void {
    const params = new HttpParams().set('size', '9999');
    this.employeeId = Number(this.route.snapshot.paramMap.get('employeeId'));
    if (this.employeeId) {
      this.loadData(this.employeeId);
      this.loadNotes(this.employeeId, params);
    } 
  }

  loadData(employeeId: number) {
    this.employeeConsultantsService.detail(employeeId).subscribe((response: any) => {
      if (response?.data) {
        this.employee = response.data.employee;
      }
    });

    this.employeeConsultantsService.listEmployeeJobs(employeeId).subscribe((response: any) => {
      if (response?.data?.employeeJobs) {
        this.employeeWorkCustomerList = response.data.employeeJobs || [];
      }
    });
  }

  loadNotes(employeeId: any, params: HttpParams) {
    this.employeeConsultantsService.listNotes(employeeId, { params }).subscribe(response => {
      if (response.status.code === 200) {        
        this.employeeNotes = response?.data.sectionNotes || [];
      }
    });
  }
  
  private showComponentAlert(component: any, type: string, message: string): void {
    const componentId = this.getComponentIdentifier(component);
    if (this.alertTimers.has(componentId)) {
      clearTimeout(this.alertTimers.get(componentId));
      this.alertTimers.delete(componentId);
    }
    
    if (component && component.showAlertInfo) {
      component.showAlertInfo(type, message);
      
      const timerId = setTimeout(() => {
        if (component && component.showAlertInfo) {
          component.showAlertInfo('', ''); 
        }
        this.alertTimers.delete(componentId);
      }, 5000);
      
      this.alertTimers.set(componentId, timerId);
    }
  }
  
  private getComponentIdentifier(component: any): string {
    if (component === this.residentialInfoFormComponent) return 'residential';
    if (component === this.generalInfoFormComponent) return 'general';
    if (component === this.previousJobsFormComponent) return 'jobs';
    return 'unknown';
  }
  
  private closeAllAlerts(): void {
    const components = [
      this.residentialInfoFormComponent,
      this.generalInfoFormComponent,
      this.previousJobsFormComponent
    ];
    
    components.forEach(component => {
      if (component && component.showAlertInfo) {
        component.showAlertInfo('', ''); 
      }
      
      const componentId = this.getComponentIdentifier(component);
      if (this.alertTimers.has(componentId)) {
        clearTimeout(this.alertTimers.get(componentId));
        this.alertTimers.delete(componentId);
      }
    });
  }
  
  onResidentialFormSavedData(savedDataInfo: SavedDataInfo) {
    if (savedDataInfo.success) {
      const employeeId = savedDataInfo.response?.data?.employee?.employeeId || this.employeeId;
      if (employeeId) {
        this.loadData(employeeId);
      }
      this.showComponentAlert(this.residentialInfoFormComponent, 'success', 'Residential information saved successfully');
    } else {
      this.showComponentAlert(this.residentialInfoFormComponent, 'danger', 'Error saving residential information');
    }
  }
  
  onGeneralFormSavedData(savedDataInfo: SavedDataInfo) {
    if (savedDataInfo.success) {
      const employeeId = savedDataInfo.response?.data?.employee?.employeeId || this.employeeId;
      if (employeeId) {
        this.loadData(employeeId);
      }
      this.showComponentAlert(this.generalInfoFormComponent, 'success', 'General information saved successfully');
    } else {
      this.showComponentAlert(this.generalInfoFormComponent, 'danger', 'Error saving general information');
    }
  }
  
  onPreviousJobsFormSavedData(savedDataInfo: SavedDataInfo) {
    if (savedDataInfo.success) {
      const employeeId = savedDataInfo.response?.data?.employee?.employeeId || this.employeeId;
      if (employeeId) {
        this.loadData(employeeId);
      }
      this.showComponentAlert(this.previousJobsFormComponent, 'success', 'Previous jobs information saved successfully');
    } else {
      this.showComponentAlert(this.previousJobsFormComponent, 'danger', 'Error saving previous jobs information');
    }
  }
  
  onDocumentsSavedData(savedDataInfo: SavedDataInfo) {
    if (savedDataInfo.success) {
      this.showComponentAlert(this.documentsComponent, 'success', 'Document uploaded successfully');
    } else {
      this.showComponentAlert(this.documentsComponent, 'danger', 'Error uploading document');
    }
  }
  
  saveAllForms(): void {
    if (this.isSaving) return;
    this.isSaving = true;
    
    if (!this.residentialInfoFormComponent.validateForm()) {
      this.isSaving = false;
      this.showComponentAlert(this.residentialInfoFormComponent, 'danger', 'Please fill in all required fields correctly');
      return;
    }

    if (!this.generalInfoFormComponent.validateForm()) {
      this.isSaving = false;
      this.showComponentAlert(this.generalInfoFormComponent, 'danger', 'Please fill in all required fields correctly');
      return;
    }

    if (!this.previousJobsFormComponent.validateForm()) {
      this.isSaving = false;
      this.showComponentAlert(this.previousJobsFormComponent, 'danger', 'Please fill in all required fields correctly');
      return;
    }

    const residentialData = this.residentialInfoFormComponent.form.value;
    const generalData = this.generalInfoFormComponent.form.value;
    
    const combinedData = {
      ...residentialData,
      ...generalData,
    };
    
    if (this.employeeId) {
      this.employeeConsultantsService.update(this.employeeId, combinedData).subscribe({
        next: (response) => this.handleSuccessfulSave(response),
        error: (error) => this.handleSaveError(error)
      });
    } else {
      this.employeeConsultantsService.create(combinedData).subscribe({
        next: (response) => this.handleSuccessfulSave(response),
        error: (error) => this.handleSaveError(error)
      });
    }
  }
  
  private handleSuccessfulSave(response: any): void {
    if (!this.employeeId && response?.data?.employee?.employeeId) {
      this.employeeId = response.data.employee.employeeId;
    }
    
    if (this.employeeId) {
      this.generalInfoFormComponent.saveEmployeeSpecializations(this.employeeId).subscribe({
        next: () => {
          this.isSaving = false;
          this.loadData(this.employeeId);
          this.alertInfo = { 
            show: true, 
            type: 'success', 
            message: 'All information has been successfully saved' 
          };
          setTimeout(() => this.alertInfo.show = false, 3000);
          
          this.showComponentAlert(this.residentialInfoFormComponent, 'success', 'Residential information saved successfully');
          this.showComponentAlert(this.generalInfoFormComponent, 'success', 'General information saved successfully');
          this.showComponentAlert(this.previousJobsFormComponent, 'success', 'Previous jobs information saved successfully');
        },
        error: (error) => {
          this.isSaving = false;
          this.showComponentAlert(this.generalInfoFormComponent, 'danger', error.error?.message || 'Error saving general information');
        }
      });
    } else {
      this.isSaving = false;
      this.loadData(this.employeeId);
      this.alertInfo = { 
        show: true, 
        type: 'success', 
        message: 'All information has been successfully saved' 
      };
      setTimeout(() => this.alertInfo.show = false, 3000);
    }
  }
  
  private handleSaveError(error: any): void {
    this.isSaving = false;
    this.alertInfo = { 
      show: true, 
      type: 'danger', 
      message: error.error.error?.message || 'An error occurred while saving the information' 
    };
    setTimeout(() => this.alertInfo.show = false, 3000);
  }

  
}
